import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import {
  Autocomplete,
  TextField,
  Chip,
  Box,
  Checkbox,
  FormControlLabel,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import Spinner from 'src/components/Spinner/Spinner';
import {
  getAllGroupsList,
  getContactGroups,
  setValuesFormAddToGroup,
} from 'src/actions/customerDetailsActions';

function AddToGroupModalChat({
  classes,
  loading,
  group,
  contactGroups,
  contactId,
  readdConfirmed,
  onReaddConfirmedChange,
}) {
  const dispatch = useDispatch();

  const {
    groups,
  } = useSelector((state) => state?.customerDetails || {});

  const contactGroupIds = contactGroups.map((contactGroup) => contactGroup.groupId);

  const groupList = groups && groups
    .filter((groupOption) => String(groupOption.status || '').toLowerCase() !== 'inactive')
    .map((groupOption) => ({
      value: groupOption.id,
      label: groupOption.groupName,
      groupName: groupOption.groupName,
      isAlreadyMember: contactGroupIds.includes(groupOption.id),
    }))
    .sort((a, b) => a.groupName.localeCompare(b.groupName));

  const handleOnChange = (event, value) => {
    dispatch(setValuesFormAddToGroup({
      group: value,
    }));
    // Reset confirmation when group selection changes
    if (onReaddConfirmedChange) {
      onReaddConfirmedChange(false);
    }
  };

  const getOptionLabel = (option) => {
    if (!option) return '';
    if (option.label) {
      return option.label;
    }
    if (option.length !== 0) {
      return option;
    }
    return '';
  };

  const renderOption = (props, option) => (
    <li {...props}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <span>{option.label}</span>
        {option.isAlreadyMember && (
          <Chip
            label="Already a member"
            size="small"
            color="success"
            sx={{ ml: 1, height: 24, fontSize: '0.75rem' }}
          />
        )}
      </Box>
    </li>
  );

  const handleReaddConfirmChange = (event) => {
    if (onReaddConfirmedChange) {
      onReaddConfirmedChange(event.target.checked);
    }
  };

  useEffect(() => {
    dispatch(getAllGroupsList());
    if (contactId) {
      dispatch(getContactGroups(contactId));
    }

    return () => {
      dispatch(setValuesFormAddToGroup({
        group: '',
      }));
    };
  }, [dispatch, contactId]);

  const isAlreadyMemberSelected = group?.isAlreadyMember || false;

  return (
    <div className={classes.form}>
      <Spinner
        loading={loading}
        positionType="absolute"
      />
      <Grid
        container
        spacing={5}
        className={`${classes.groupLabelText} mt0 semiBold`}
      >
        <Grid
          xs={12}
          className="pt0"
        >
          <p className="text-style-small-1">
            Enter and/or Select a group to add the contact to.
          </p>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={8}
      >
        <Grid xs={12}>
          <Autocomplete
            disabled={loading}
            options={groupList || []}
            getOptionLabel={getOptionLabel}
            renderOption={renderOption}
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            clearOnEscape
            renderInput={(params) => (
              <TextField
                fullWidth
                label="Enter & Select a Group"
                {...params}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {group?.isAlreadyMember && (
                        <Chip
                          label="Already a member"
                          size="small"
                          color="success"
                          sx={{ mr: 1, height: 24, fontSize: '0.75rem' }}
                        />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            value={group || null}
            onChange={handleOnChange}
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={5}
        className={`${classes.groupLabelText} mt0 semiBold`}
      >
        <Grid xs={12}>
          <p className="text-style-small-1">
            Note: If the group is not in this list, go to the CP Groups screen, change the status to &quot;Active&quot; --OR-- Add the group if it doesn&apos;t exist, then return here to add the contact to the group.
          </p>
        </Grid>
      </Grid>
      {isAlreadyMemberSelected && (
        <Grid
          container
          spacing={5}
        >
          <Grid xs={12}>
            <Alert
              severity="warning"
              sx={{ mt: 1, mb: -4 }}
            >
              <Box>
                This contact is already a member of this group. Adding them again will remove them from the group and then re-add them, which will re-execute any join actions if selected.
              </Box>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={readdConfirmed}
                    onChange={handleReaddConfirmChange}
                    color="warning"
                  />
                )}
                label="I understand, proceed with re-adding"
                sx={{ mt: 1 }}
              />
            </Alert>
          </Grid>
        </Grid>
      )}
    </div>
  );
}

AddToGroupModalChat.propTypes = {
  classes: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  contactGroups: PropTypes.array,
  contactId: PropTypes.number,
  group: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
  ]),
  readdConfirmed: PropTypes.bool,
  onReaddConfirmedChange: PropTypes.func,
};

AddToGroupModalChat.defaultProps = {
  contactGroups: [],
  contactId: null,
  group: null,
  readdConfirmed: false,
  onReaddConfirmedChange: () => { },
};

export default AddToGroupModalChat;
